import React, { useState, useEffect, useRef } from 'react';
import { WallConfig, PresetRatio, CalculationResult } from '../types';
import { trackEvent } from '../analytics';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface SidebarProps {
  config: WallConfig;
  setConfig: React.Dispatch<React.SetStateAction<WallConfig>>;
  results: CalculationResult;
}

const CABINET_SIZES = [
  "500x500", "1000x500", "500x1000", "1000x1000",
  "600x600", "600x1200", "1200x600", "1200x1200"
];

const PIXEL_PITCHES = [
  "1.9", "2.6", "2.9", "3.9", "4.8", "5.2", "7.5", "7.8", "10.4", "CUSTOM"
];

const ACTUAL_PITCH_MAP: Record<string, number> = {
  "1.9": 500 / 256,
  "2.6": 500 / 192,
  "2.9": 500 / 168,
  "3.9": 500 / 128,
  "4.8": 500 / 104,
  "5.2": 500 / 96,
  "7.5": 600 / 80,
  "7.8": 500 / 64,
  "10.4": 500 / 48,
};

const Sidebar: React.FC<SidebarProps> = ({ config, setConfig, results }) => {
  const formatVal = (val: number, decimals: number = 2) => {
    return parseFloat(val.toFixed(decimals)).toString();
  };

  const [localWidthM, setLocalWidthM] = useState(formatVal(results.physicalWidthM, 2));
  const [localHeightM, setLocalHeightM] = useState(formatVal(results.physicalHeightM, 2));
  const [errors, setErrors] = useState<{ width?: string; height?: string }>({});
  
  const widthInputRef = useRef<HTMLInputElement>(null);
  const heightInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (document.activeElement !== widthInputRef.current) {
      setLocalWidthM(formatVal(results.physicalWidthM, 2));
    }
    if (document.activeElement !== heightInputRef.current) {
      setLocalHeightM(formatVal(results.physicalHeightM, 2));
    }
  }, [results.physicalWidthM, results.physicalHeightM]);

  const updateConfig = (updates: Partial<WallConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const getTruePitch = (nominal: string): number => {
    return ACTUAL_PITCH_MAP[nominal] || parseFloat(nominal) || 3.90625;
  };

  const handleMeterInputChange = (value: string, type: 'width' | 'height') => {
    if (type === 'width') setLocalWidthM(value);
    else setLocalHeightM(value);

    const meters = parseFloat(value);
    if (isNaN(meters) || meters <= 0) return;

    const [w, h] = config.cabinetSizePreset.split('x').map(Number);
    const cabSizeM = (type === 'width' ? w : h) / 1000;
    
    const cabinetCount = meters / cabSizeM;
    const roundedCount = Math.round(cabinetCount);
    const isMultiple = Math.abs(cabinetCount - roundedCount) < 0.001;

    if (!isMultiple && value.length > 1) {
      setErrors(prev => ({
        ...prev,
        [type]: `Note: ${meters}m is not a perfect multiple of ${formatVal(cabSizeM, 2)}m panels.`
      }));
    } else {
      setErrors(prev => ({ ...prev, [type]: undefined }));
    }

    if (type === 'width') updateConfig({ cabinetsWide: Math.max(0, roundedCount) });
    else updateConfig({ cabinetsHigh: Math.max(0, roundedCount) });
    
    trackEvent('input_manual_dimension', { dimension: type, value: meters });
  };

  const handleHardwareChange = (sizePreset: string, pitchPreset: string) => {
    const [w, h] = sizePreset.split('x').map(Number);
    
    if (pitchPreset === "CUSTOM") {
      updateConfig({
        cabinetSizePreset: sizePreset,
        pixelPitchPreset: pitchPreset,
      });
      return;
    }

    const pitch = getTruePitch(pitchPreset);
    updateConfig({
      cabinetSizePreset: sizePreset,
      pixelPitchPreset: pitchPreset,
      pixelsWide: Math.round(w / pitch),
      pixelsHigh: Math.round(h / pitch)
    });
  };

  const handlePresetSelect = (preset: PresetRatio) => {
    let ratio = config.customRatio;
    let decimal = config.customDecimal;
    switch (preset) {
      case PresetRatio.RATIO_16_9: ratio = "16:9"; decimal = "1.778"; break;
      case PresetRatio.RATIO_9_16: ratio = "9:16"; decimal = "0.563"; break;
      case PresetRatio.RATIO_21_9: ratio = "21:9"; decimal = "2.333"; break;
      case PresetRatio.RATIO_4_3: ratio = "4:3"; decimal = "1.333"; break;
      default: break;
    }
    updateConfig({ targetRatio: preset, customRatio: ratio, customDecimal: decimal });
  };

  const handleCustomRatioChange = (val: string) => {
    const updates: Partial<WallConfig> = { 
      targetRatio: PresetRatio.CUSTOM, 
      customRatio: val, 
      lastEditedCustom: 'ratio' 
    };
    
    if (val.includes(':')) {
      const [w, h] = val.split(':').map(parseFloat);
      if (!isNaN(w) && !isNaN(h) && h !== 0) {
        updates.customDecimal = formatVal(w / h, 3);
      }
    } else {
      const dec = parseFloat(val);
      if (!isNaN(dec)) {
        updates.customDecimal = formatVal(dec, 3);
      }
    }
    updateConfig(updates);
  };

  const handleCustomDecimalChange = (val: string) => {
    const updates: Partial<WallConfig> = { 
      targetRatio: PresetRatio.CUSTOM, 
      customDecimal: val, 
      lastEditedCustom: 'decimal' 
    };
    
    const dec = parseFloat(val);
    if (!isNaN(dec)) {
      updates.customRatio = `${formatVal(dec, 3)}:1`;
    }
    updateConfig(updates);
  };

  const ratioPresets = [
    { label: "16:9", value: PresetRatio.RATIO_16_9 },
    { label: "9:16", value: PresetRatio.RATIO_9_16 },
    { label: "21:9", value: PresetRatio.RATIO_21_9 },
    { label: "4:3", value: PresetRatio.RATIO_4_3 },
    { label: "CUSTOM", value: PresetRatio.CUSTOM },
  ];

  const ControlButtons = ({ onInc, onDec }: { onInc: () => void, onDec: () => void }) => (
    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-40 hover:opacity-100 transition-opacity">
      <button 
        onClick={onInc}
        className="p-0.5 hover:bg-slate-800 rounded text-slate-400 hover:text-[#ff8000] transition-colors"
      >
        <ChevronUp size={12} strokeWidth={3} />
      </button>
      <button 
        onClick={onDec}
        className="p-0.5 hover:bg-slate-800 rounded text-slate-400 hover:text-[#ff8000] transition-colors"
      >
        <ChevronDown size={12} strokeWidth={3} />
      </button>
    </div>
  );

  return (
    <aside className="w-full md:w-80 bg-slate-900 border-r border-slate-800 p-6 flex flex-col gap-8 shrink-0 md:max-h-screen md:overflow-y-auto scrolling-touch">
      {/* APP EXPLAINER */}
      <section className="bg-slate-950/80 border border-[#ff8000]/20 p-4 rounded-xl shadow-inner shadow-black/20">
        <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
          <span className="text-[#ff8000] font-black block mb-2 uppercase tracking-[0.15em] border-b border-[#ff8000]/10 pb-2">How it works</span>
          LED Vision is designed for LED techs and media server operators to bridge the gap between content aspect ratios and physical screen hardware.
        </p>
      </section>

      {/* 01. SOURCE DEFINITION */}
      <section className="space-y-4">
        <header className="flex items-center gap-2">
          <span className="text-[10px] bg-blue-500/20 text-blue-400 w-5 h-5 flex items-center justify-center rounded font-bold">01</span>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Source Content</label>
        </header>
        <div className="grid grid-cols-3 gap-1.5">
          {ratioPresets.map(p => (
            <button
              key={p.value}
              onClick={() => handlePresetSelect(p.value as PresetRatio)}
              className={`px-1 py-2 text-[10px] font-mono-data rounded border transition-all ${
                config.targetRatio === p.value
                  ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                  : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:border-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 p-3 bg-slate-950/50 rounded-lg border border-slate-800">
          <div className="space-y-1">
            <span className="text-[9px] text-slate-600 uppercase font-bold block">Ratio (W:H)</span>
            <input
              type="text"
              value={config.customRatio}
              onChange={(e) => handleCustomRatioChange(e.target.value)}
              onFocus={() => updateConfig({ targetRatio: PresetRatio.CUSTOM })}
              className={`w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 font-mono-data text-xs text-slate-300 focus:outline-none focus:border-blue-500 ${config.targetRatio !== PresetRatio.CUSTOM ? 'opacity-60' : ''}`}
            />
          </div>
          <div className="space-y-1">
            <span className="text-[9px] text-slate-600 uppercase font-bold block">Decimal</span>
            <input
              type="text"
              value={config.customDecimal}
              onChange={(e) => handleCustomDecimalChange(e.target.value)}
              onFocus={() => updateConfig({ targetRatio: PresetRatio.CUSTOM })}
              className={`w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 font-mono-data text-xs text-slate-300 focus:outline-none focus:border-blue-500 ${config.targetRatio !== PresetRatio.CUSTOM ? 'opacity-60' : ''}`}
            />
          </div>
        </div>
      </section>

      {/* 02. HARDWARE DEFINITION */}
      <section className="space-y-4">
        <header className="flex items-center gap-2">
          <span className="text-[10px] bg-emerald-500/20 text-emerald-400 w-5 h-5 flex items-center justify-center rounded font-bold">02</span>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Hardware</label>
        </header>
        <div className="space-y-3">
          <div>
            <span className="text-[10px] text-slate-500 block mb-1.5 ml-1">Panel Size (mm)</span>
            <select 
              value={config.cabinetSizePreset}
              onChange={(e) => handleHardwareChange(e.target.value, config.pixelPitchPreset)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-white font-mono-data text-xs focus:outline-none focus:border-emerald-500"
            >
              {CABINET_SIZES.map(s => <option key={s} value={s}>{s}mm</option>)}
            </select>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 block mb-1.5 ml-1">Pixel Pitch (mm)</span>
            <div className="grid grid-cols-4 gap-1">
              {PIXEL_PITCHES.map(p => (
                <button
                  key={p}
                  onClick={() => handleHardwareChange(config.cabinetSizePreset, p)}
                  className={`py-1 text-[9px] font-mono-data border rounded transition-all ${
                    config.pixelPitchPreset === p
                      ? 'bg-emerald-500 border-emerald-500 text-slate-950 font-bold'
                      : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:border-slate-600'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          
          {/* Panel Resolution Display/Edit */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-slate-950/50 rounded-lg border border-slate-800 mt-3">
            <div className="space-y-1">
              <span className="text-[9px] text-slate-600 uppercase font-bold block">Panel Width (px)</span>
              <input
                type="number"
                value={config.pixelsWide || ''}
                onChange={(e) => updateConfig({ pixelsWide: parseInt(e.target.value) || 0, pixelPitchPreset: "CUSTOM" })}
                onFocus={() => updateConfig({ pixelPitchPreset: "CUSTOM" })}
                className={`w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 font-mono-data text-xs text-slate-300 focus:outline-none focus:border-emerald-500 ${config.pixelPitchPreset !== "CUSTOM" ? 'opacity-60' : ''}`}
              />
            </div>
            <div className="space-y-1">
              <span className="text-[9px] text-slate-600 uppercase font-bold block">Panel Height (px)</span>
              <input
                type="number"
                value={config.pixelsHigh || ''}
                onChange={(e) => updateConfig({ pixelsHigh: parseInt(e.target.value) || 0, pixelPitchPreset: "CUSTOM" })}
                onFocus={() => updateConfig({ pixelPitchPreset: "CUSTOM" })}
                className={`w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 font-mono-data text-xs text-slate-300 focus:outline-none focus:border-emerald-500 ${config.pixelPitchPreset !== "CUSTOM" ? 'opacity-60' : ''}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 03. LAYOUT CONFIGURATION */}
      <section className="space-y-4">
        <header className="flex items-center gap-2">
          <span className="text-[10px] bg-[#ff8000]/20 text-[#ff8000] w-5 h-5 flex items-center justify-center rounded font-bold">03</span>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Layout Configuration</label>
        </header>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <span className="text-[10px] text-slate-500 uppercase block ml-1">Panels Wide</span>
            <div className="relative group">
              <input
                type="number"
                value={config.cabinetsWide || ''}
                onChange={(e) => updateConfig({ cabinetsWide: parseInt(e.target.value) || 0 })}
                className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 pr-8 text-white font-mono-data text-xs focus:outline-none focus:border-[#ff8000] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <ControlButtons 
                onInc={() => updateConfig({ cabinetsWide: (config.cabinetsWide || 0) + 1 })}
                onDec={() => updateConfig({ cabinetsWide: Math.max(0, (config.cabinetsWide || 0) - 1) })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] text-slate-500 uppercase block ml-1">Panels High</span>
            <div className="relative group">
              <input
                type="number"
                value={config.cabinetsHigh || ''}
                onChange={(e) => updateConfig({ cabinetsHigh: parseInt(e.target.value) || 0 })}
                className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 pr-8 text-white font-mono-data text-xs focus:outline-none focus:border-[#ff8000] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <ControlButtons 
                onInc={() => updateConfig({ cabinetsHigh: (config.cabinetsHigh || 0) + 1 })}
                onDec={() => updateConfig({ cabinetsHigh: Math.max(0, (config.cabinetsHigh || 0) - 1) })}
              />
            </div>
          </div>
        </div>
        <div className="pt-2 border-t border-slate-800/50">
          <span className="text-[10px] text-[#ff8000]/80 font-bold uppercase block mb-2 ml-1">Physical Dimensions (Meters)</span>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className="text-[8px] text-slate-600 uppercase block">Width (m)</span>
              <div className="relative group">
                <input
                  ref={widthInputRef}
                  type="text"
                  value={localWidthM}
                  onChange={(e) => handleMeterInputChange(e.target.value, 'width')}
                  onBlur={() => setLocalWidthM(formatVal(results.physicalWidthM, 2))}
                  className={`w-full bg-slate-950 border ${errors.width ? 'border-rose-500' : 'border-slate-800'} rounded px-3 py-2 pr-8 text-[#ff8000] font-mono-data text-xs focus:outline-none`}
                />
                <ControlButtons 
                  onInc={() => updateConfig({ cabinetsWide: (config.cabinetsWide || 0) + 1 })}
                  onDec={() => updateConfig({ cabinetsWide: Math.max(0, (config.cabinetsWide || 0) - 1) })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-[8px] text-slate-600 uppercase block">Height (m)</span>
              <div className="relative group">
                <input
                  ref={heightInputRef}
                  type="text"
                  value={localHeightM}
                  onChange={(e) => handleMeterInputChange(e.target.value, 'height')}
                  onBlur={() => setLocalHeightM(formatVal(results.physicalHeightM, 2))}
                  className={`w-full bg-slate-950 border ${errors.height ? 'border-rose-500' : 'border-slate-800'} rounded px-3 py-2 pr-8 text-[#ff8000] font-mono-data text-xs focus:outline-none`}
                />
                <ControlButtons 
                  onInc={() => updateConfig({ cabinetsHigh: (config.cabinetsHigh || 0) + 1 })}
                  onDec={() => updateConfig({ cabinetsHigh: Math.max(0, (config.cabinetsHigh || 0) - 1) })}
                />
              </div>
            </div>
          </div>
          {(errors.width || errors.height) && <p className="mt-2 text-[8px] text-rose-400 italic leading-tight">* {errors.width || errors.height}</p>}
        </div>
      </section>
    </aside>
  );
};

export default Sidebar;